/**
 * File Validation Utility
 *
 * Frontend file validation to block malicious files before upload.
 * Provides extension validation, MIME type checking, and magic number verification.
 */

import { DEFAULT_FILE_VALIDATION_CONFIG, type FileValidationConfig } from "@/config/fileValidation";
import { logger } from "@/telemetry";

export interface FileValidationResult {
  /** Whether all files passed validation */
  isValid: boolean;
  /** Array of validation errors for failed files */
  errors: FileValidationError[];
  /** Optional warnings (non-blocking) */
  warnings?: string[];
}

export interface FileValidationError {
  /** The file that failed validation */
  file: File;
  /** Type of validation that failed */
  errorType:
    | "blocked_extension"
    | "mime_mismatch"
    | "magic_number_mismatch"
    | "extension_not_allowed";
  /** User-friendly error message */
  message: string;
  /** Technical details for debugging */
  technicalDetails?: string;
}

/**
 * Validates multiple files against security rules
 *
 * @param files - Array of files to validate
 * @param config - Optional validation configuration (uses defaults if not provided)
 * @returns Validation result with any errors found
 */
export async function validateFiles(
  files: File[],
  config: Partial<FileValidationConfig> = {},
): Promise<FileValidationResult> {
  const validationConfig = { ...DEFAULT_FILE_VALIDATION_CONFIG, ...config };
  const errors: FileValidationError[] = [];

  // Validate each file
  for (const file of files) {
    // Extension validation
    const extensionError = validateExtension(file, validationConfig);

    if (extensionError) {
      errors.push(extensionError);
      continue; // Skip other validations if extension is blocked
    }

    // MIME type validation
    const mimeError = validateMimeType(file, validationConfig);

    if (mimeError) {
      errors.push(mimeError);
      continue; // Skip magic number check if MIME fails
    }

    // Magic number validation (if enabled)
    if (validationConfig.enableMagicNumberValidation) {
      const magicError = await validateMagicNumber(file, validationConfig);

      if (magicError) {
        errors.push(magicError);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates file extension against allow/block lists
 *
 * @param file - File to validate
 * @param config - Validation configuration
 * @returns Error if extension is invalid, null if valid
 */
export function validateExtension(
  file: File,
  config: FileValidationConfig = DEFAULT_FILE_VALIDATION_CONFIG,
): FileValidationError | null {
  const extension = getFileExtension(file.name);

  // Check if extension is explicitly blocked
  if (config.blockedExtensions.includes(extension)) {
    return {
      file,
      errorType: "blocked_extension",
      message: `File type "${extension}" is not allowed for security reasons. Please upload a supported file type.`,
      technicalDetails: `Extension "${extension}" is in blocked list`,
    };
  }

  // Check if extension is in allowed list
  if (!config.allowedExtensions.includes(extension)) {
    const supportedTypes = config.allowedExtensions.join(", ");

    return {
      file,
      errorType: "extension_not_allowed",
      message: `File type "${extension}" is not supported. Supported types: ${supportedTypes}`,
      technicalDetails: `Extension "${extension}" not in allowed list`,
    };
  }

  return null;
}

/**
 * Validates MIME type matches expected type for file extension
 *
 * @param file - File to validate
 * @param config - Validation configuration
 * @returns Error if MIME type is invalid, null if valid
 */
export function validateMimeType(
  file: File,
  config: FileValidationConfig = DEFAULT_FILE_VALIDATION_CONFIG,
): FileValidationError | null {
  const extension = getFileExtension(file.name);
  const fileMimeType = file.type;

  // Skip validation if no MIME type expected for this extension
  const expectedMimeTypes = config.allowedMimeTypes[extension];

  if (!expectedMimeTypes || expectedMimeTypes.length === 0) {
    return null;
  }

  // Skip validation if browser doesn't provide MIME type
  if (!fileMimeType) {
    return null;
  }

  // Check if file's MIME type matches any expected type
  if (!expectedMimeTypes.includes(fileMimeType)) {
    return {
      file,
      errorType: "mime_mismatch",
      message: `File "${file.name}" appears to be corrupted or has an incorrect extension. Please verify the file is not damaged.`,
      technicalDetails: `Expected MIME types: ${expectedMimeTypes.join(", ")}, got: ${fileMimeType}`,
    };
  }

  return null;
}

/**
 * Validates file content using magic numbers (file signatures)
 *
 * @param file - File to validate
 * @param config - Validation configuration
 * @returns Promise resolving to error if magic number is invalid, null if valid
 */
export async function validateMagicNumber(
  file: File,
  config: FileValidationConfig = DEFAULT_FILE_VALIDATION_CONFIG,
): Promise<FileValidationError | null> {
  const extension = getFileExtension(file.name);
  const expectedMagicNumbers = config.magicNumbers[extension];

  // Skip validation if no magic numbers defined for this extension
  if (!expectedMagicNumbers || expectedMagicNumbers.length === 0) {
    return null;
  }

  try {
    // Read first 8 bytes (enough for most magic numbers)
    const magicBytes = await readMagicNumber(file, 8);

    // Check if any expected magic number matches
    const matches = expectedMagicNumbers.some((expectedBytes) =>
      compareMagicNumbers(magicBytes, [expectedBytes]),
    );

    if (!matches) {
      return {
        file,
        errorType: "magic_number_mismatch",
        message: `File "${file.name}" failed content verification. The file may be corrupted or have an incorrect extension.`,
        technicalDetails: `Magic number mismatch. Expected one of: ${expectedMagicNumbers.map((bytes) => bytes.map((b) => b.toString(16).padStart(2, "0")).join(" ")).join(" or ")}, got: ${magicBytes.map((b) => b.toString(16).padStart(2, "0")).join(" ")}`,
      };
    }
  } catch (error) {
    // Don't fail validation if we can't read the file
    // This could be due to browser limitations or file access issues
    logger.warn(`Failed to read magic number for ${file.name}:`, { error: String(error) });
  }

  return null;
}

/**
 * Extracts file extension from filename
 *
 * @param filename - Name of the file
 * @returns Lowercase file extension including the dot (e.g., ".pdf")
 */
export function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf(".");

  if (lastDotIndex === -1) return "";

  return filename.slice(lastDotIndex).toLowerCase();
}

/**
 * Checks if file extension is in the blocked list
 *
 * @param extension - File extension to check
 * @param config - Validation configuration
 * @returns True if extension is blocked
 */
export function isExtensionBlocked(
  extension: string,
  config: FileValidationConfig = DEFAULT_FILE_VALIDATION_CONFIG,
): boolean {
  return config.blockedExtensions.includes(extension.toLowerCase());
}

/**
 * Checks if file extension is in the allowed list
 *
 * @param extension - File extension to check
 * @param config - Validation configuration
 * @returns True if extension is allowed
 */
export function isExtensionAllowed(
  extension: string,
  config: FileValidationConfig = DEFAULT_FILE_VALIDATION_CONFIG,
): boolean {
  return config.allowedExtensions.includes(extension.toLowerCase());
}

/**
 * Reads the first N bytes of a file to check magic number
 *
 * @param file - File to read from
 * @param bytesToRead - Number of bytes to read from start of file
 * @returns Promise resolving to array of bytes
 */
export async function readMagicNumber(file: File, bytesToRead: number): Promise<number[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer);

        resolve(Array.from(bytes));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${reader.error?.message || "Unknown error"}`));
    };

    // Read only the first N bytes
    const blob = file.slice(0, bytesToRead);

    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Compares file bytes against expected magic number patterns
 *
 * @param fileBytes - Actual bytes from file
 * @param expectedPatterns - Array of possible byte patterns to match
 * @returns True if any pattern matches
 */
export function compareMagicNumbers(fileBytes: number[], expectedPatterns: number[][]): boolean {
  return expectedPatterns.some((pattern) => {
    // Check if file has enough bytes for this pattern
    if (fileBytes.length < pattern.length) return false;

    // Compare each byte in the pattern
    return pattern.every((expectedByte, index) => fileBytes[index] === expectedByte);
  });
}

/**
 * Formats a validation error for display to users
 *
 * @param error - Validation error to format
 * @returns Formatted error message
 */
export function formatValidationError(error: FileValidationError): string {
  return error.message;
}
